import { BaseApi } from './_base-api';
import { Injectable } from '@angular/core';
import { BlogCreate, BlogDetails, BlogDraftCreate, BlogEntity, BlogUpdate } from '@models';
import { map } from 'rxjs';
import { DataGetOptions, DataGetFnResponse } from '@controladad/ng-base';
import { GetApiAdapterOptionsToQueryParam } from '../adapters';

// Blog API Response Types
interface BlogApiPagination {
  page: number;
  amountOfListPerPage: number;
  total: number;
  pageCount: number;
}

// API returns BlogEntity with additional description field
interface BlogApiEntity extends BlogEntity {
  description?: string;
}

interface BlogApiDataWithPagination {
  data: BlogApiEntity[];
  pagination: BlogApiPagination;
}

interface BlogApiResponse {
  dataWithPagination: BlogApiDataWithPagination;
  draftBlogsCount: number;
  disabledBlogsCount: number;
}

// API returns BlogDetails with description instead of subTitle
interface BlogApiDetails extends Omit<BlogDetails, 'subTitle'> {
  description: string;
}

@Injectable({
  providedIn: 'root',
})
export class BlogApiService extends BaseApi<BlogEntity, BlogCreate, BlogUpdate> {
  constructor() {
    super('/Blog', {
      get: '/AdminPanel/GetBlogs',
      post: '/AdminPanel/AddBlog',
    });
  }

  override getAdapter(options?: DataGetOptions<BlogEntity>) {
    return (req?: any): DataGetFnResponse<BlogEntity> => {
      const query = GetApiAdapterOptionsToQueryParam(req, options?.default);
      return http$
        .get<BlogApiResponse>(`${this.endpoint.get}${!this.endpoint.get.includes('?') ? '?' : '&'}${query}`)
        .pipe(
          map((response: BlogApiResponse) => ({
            data: response.dataWithPagination.data.map((blog: BlogApiEntity): BlogEntity => {
              // Remove description field since BlogEntity doesn't have it
              const { description, ...blogEntity } = blog;
              return blogEntity;
            }),
            pagination: {
              page: response.dataWithPagination.pagination.page,
              total: response.dataWithPagination.pagination.total,
              size: response.dataWithPagination.pagination.amountOfListPerPage,
              totalPages: response.dataWithPagination.pagination.pageCount,
            },
            extra: {
              draftBlogsCount: response.draftBlogsCount,
              disabledBlogsCount: response.disabledBlogsCount,
              totalBlogs: response.dataWithPagination.pagination.total,
            },
          }))
        );
    };
  }
  getAllBlogs<T = BlogEntity>() {
    let queryParams = `?Pagination.Page=1&Pagination.AmountOfListPerPage=999999`;
    return http$.get<{ dataWithPagination: { data: T[]; } }>(`${this.endpoint.get}${queryParams}`).pipe(map((i) => i.dataWithPagination.data));
  }
  override create<T = BlogEntity>(model: BlogCreate) {
    return http$.post<T>('/AdminPanel/AddBlog', model);
  }

  updateBlog(model: BlogUpdate) {
    console.log(model);
    return http$.patch<never>('/AdminPanel/EditBlog', model);
  }

  saveDraft(model: BlogDraftCreate) {
    console.log('Saving draft:', model);
    return http$.post<never>('/AdminPanel/SaveDraft', model);
  }

  deleteBlog(id: number) {
    return http$.delete<never>('/AdminPanel/DeleteBlog', { body: { id: id } });
  }

  getBlogDetail(id: number) {
    return http$.get<{ blog: BlogApiDetails }>(`/AdminPanel/GetBlog?Id=${id}`).pipe(
      map((response) => ({
        blog: {
          ...response.blog,
          subTitle: response.blog.description, // Map description to subTitle
        } as BlogDetails
      }))
    );
  }

  deleteDraft(id: number) {
    return http$.delete<never>('/AdminPanel/DeleteDraft', { body: { id: id } });
  }
}

